import React from "react";
import {
  COLORS,
  SANS,
  PROVIDER_MODELS,
  PROVIDER_COLORS,
  JUDGE_COLOR,
  formStyles,
} from "./theme";

// Renders the per-provider cards. The model is not user-selectable: selecting a
// provider pins it to the admin-configured default (see defaultModelFor), which
// the parent still receives via setModels. A ⚖ button on each selected card
// sets that provider as judge (mutually exclusive). Parent owns all state.
export default function ProviderPicker({
  providers,           // available provider keys (from /providers endpoint)
  selectedProviders,
  setSelectedProviders,
  models,
  setModels,
  judgeProvider,
  setJudgeProvider,
  setJudgeModel,
  title = "AI Providers",
  hint,
  providerModels,      // admin-configured { provider: [models] }; falls back to bundled defaults
  providerDefaults,    // admin-configured { provider: defaultModelId }; falls back to first model
}) {
  // Admin-configured model lists override the bundled defaults when supplied.
  const MODELS = providerModels || PROVIDER_MODELS;
  // The pre-selected model for a provider: admin default, else its first model.
  const defaultModelFor = (p) => providerDefaults?.[p] || MODELS[p]?.[0] || "";

  // Selection is constrained to two valid states: exactly 1 provider (solo
  // evaluation) or every available provider (multi + judge). Picking a 2nd
  // provider snaps to "all"; clicking at the "all" state resets to 0 so the
  // user can re-enter solo. This keeps the count out of the invalid 2-state.
  const availableProviders = Object.keys(MODELS).filter((x) =>
    providers.includes(x)
  );

  const ensureModelsFor = (provs) => {
    setModels((prev) => {
      const next = { ...prev };
      provs.forEach((p) => {
        if (!next[p]) next[p] = defaultModelFor(p);
      });
      return next;
    });
  };

  const toggleProvider = (p) => {
    if (!availableProviders.includes(p)) return;
    const isSelected = selectedProviders.includes(p);
    const atAll =
      availableProviders.length > 0 &&
      selectedProviders.length === availableProviders.length;

    if (atAll) {
      // Any click while everyone is on → reset, force the user to re-pick.
      setSelectedProviders([]);
      setJudgeProvider?.(null);
      setJudgeModel?.(null);
      return;
    }

    if (isSelected) {
      // Turning off the only selected one → back to 0.
      const next = selectedProviders.filter((x) => x !== p);
      setSelectedProviders(next);
      if (next.length === 0) {
        setJudgeProvider?.(null);
        setJudgeModel?.(null);
      }
      return;
    }

    if (selectedProviders.length === 0) {
      // 0 → 1 (solo). No judge yet.
      setSelectedProviders([p]);
      ensureModelsFor([p]);
      setJudgeProvider?.(null);
      setJudgeModel?.(null);
      return;
    }

    // Currently at 1+ and adding another → jump straight to ALL available.
    setSelectedProviders(availableProviders);
    ensureModelsFor(availableProviders);
  };

  const handleJudgeToggle = (p, e) => {
    e.stopPropagation();
    if (judgeProvider === p) {
      setJudgeProvider(null);
      setJudgeModel?.(null);
    } else {
      setJudgeProvider(p);
      setJudgeModel?.(models[p] || defaultModelFor(p));
    }
  };

  return (
    <div style={formStyles.fieldGroup}>
      <h2 style={formStyles.sectionTitle}>{title}</h2>
      {hint && <p style={styles.hint}>{hint}</p>}

      {providers.length === 0 && (
        <p style={styles.hint}>No providers configured. Add API keys to backend .env</p>
      )}

      <div style={styles.list}>
        {Object.keys(MODELS).map((p) => {
          const available = providers.includes(p);
          const selected = selectedProviders.includes(p);
          const isJudge = judgeProvider === p;
          return (
            <div
              key={p}
              style={{
                ...styles.card,
                opacity: available ? 1 : 0.4,
                border: isJudge
                  ? `2px solid ${JUDGE_COLOR}`
                  : selected
                  ? `2px solid ${PROVIDER_COLORS[p]}`
                  : `2px solid ${COLORS.border}`,
                boxShadow: isJudge ? `0 0 0 3px ${JUDGE_COLOR}` : "none",
                cursor: available ? "pointer" : "not-allowed",
              }}
              onClick={() => available && toggleProvider(p)}
            >
              <div style={styles.row}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={selected}
                  disabled={!available}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (available) toggleProvider(p);
                  }}
                  title={selected ? `Disable ${p}` : `Enable ${p}`}
                  style={{
                    ...styles.switchTrack,
                    background: selected ? PROVIDER_COLORS[p] : "#d6d3cb",
                    cursor: available ? "pointer" : "not-allowed",
                  }}
                >
                  <span
                    style={{
                      ...styles.switchThumb,
                      transform: selected ? "translateX(16px)" : "translateX(0)",
                    }}
                  />
                </button>
                <span style={{ ...styles.dot, background: available ? PROVIDER_COLORS[p] : "#555" }} />
                <span style={styles.providerName}>{p}</span>
                {isJudge && <span style={styles.judgeTag}>⚖ JUDGE</span>}
                <div style={{ flex: 1 }} />
                {!available && <span style={styles.tag}>no key</span>}
                {available && selected && setJudgeProvider && selectedProviders.length > 1 && (
                  <button
                    type="button"
                    style={{
                      ...styles.judgeBtn,
                      background: isJudge ? JUDGE_COLOR : COLORS.dark,
                      color: isJudge ? "#000" : "#fff",
                      border: `1px solid ${isJudge ? JUDGE_COLOR : COLORS.dark}`,
                    }}
                    onClick={(e) => handleJudgeToggle(p, e)}
                    title={isJudge ? "Remove as Judge" : "Set as Judge"}
                  >
                    ⚖ {isJudge ? "Remove as Judge" : "Set as Judge"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  hint: { color: COLORS.inkSoft, fontSize: 12, marginBottom: 12, lineHeight: 1.5 },
  list: { display: "flex", flexDirection: "column", gap: 8 },
  card: {
    borderRadius: 10,
    padding: "12px 14px",
    background: COLORS.card,
    transition: "border-color 0.15s",
  },
  row: { display: "flex", alignItems: "center", gap: 10 },
  dot: { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  providerName: { fontWeight: 600, textTransform: "capitalize", fontSize: 14 },
  tag: {
    fontSize: 10,
    color: COLORS.inkMuted,
    background: COLORS.bgAlt,
    padding: "2px 8px",
    borderRadius: 4,
  },
  judgeTag: {
    fontSize: 12,
    color: "#000",
    background: JUDGE_COLOR,
    padding: "4px 14px",
    borderRadius: 999,
    fontWeight: 700,
    letterSpacing: 0.5,
  },
  judgeBtn: {
    borderRadius: 6,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: SANS,
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  },
  switchTrack: {
    position: "relative",
    width: 36,
    height: 20,
    borderRadius: 999,
    border: "none",
    padding: 0,
    flexShrink: 0,
    transition: "background 0.15s",
  },
  switchThumb: {
    position: "absolute",
    top: 2,
    left: 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
    transition: "transform 0.15s ease",
  },
};
